<template lang="pug">
    transition(
        name="_tr-modal",
        appear)
        div(
            v-show="show",
            class="_modal _con",
            :role="role || 'dialog'",
            aria-modal="true",
            :aria-labelledby="labelledby",
            :aria-describedby="describedby",
            :dir="direction",
            @keydown="keydown",
            @mousedown="_mousedown",
            @touchstart="_touchstart")
            slot(name="backdrop")
            component(ref="content",
                class="_mdlg",
                :is="action ? 'ib-form' : 'ib-form-object'",
                :style="{ width, height }",
                @success="_success",
                @error="_error")
                div(v-if="$slots.default", class="_mdef", ref="mdef")
                    slot
                div(v-if="$slots.buttons", class="_mbtn", :class="mbtn")
                    slot(name="buttons")
</template>

<script>
    import $device from '../../global/device';
    import $util from '../../global/util';

    const useBorder = ['desktop','marketing','web'];

    let open = 0;
    let openx;
    let openy;

    /**
     * DEPRECATED; use ib-dialog
     */
    export default {
        name: 'ib-modal',

        props: {
            /**
             * The aria-role to attach to the modal.
             */
            role: String,
            /**
             * The aria-label to attach to the modal.
             */
            labelledby: String,
            /**
             * The aria-description to attach to the modal.
             */
            describedby: String,
            /**
             * Width of dialog in CSS units.
             */
            width: String,
            /**
             * Height of dialog in CSS units.
             */
            height: String,
            /**
             * Toggles autoclose behavior when the user taps the foreground.
             */
            autoclose: Boolean,
            /**
             * Vuex action to dispatch on submit. If this is specified and the action completes
             * successfully, the modal will close automatically.
             */
            action: String,
            /**
             * Options to be passed with dispatched action.
             */
            options: [Object,Function]
        },

        data() {
            return {
                show: false
            };
        },

        computed: {
            direction() {
                const app = this.$root.__app;
                return app ? app.direction : 'ltr';
            },

            mbtn() {
                return useBorder.includes(this.$app.option.platform) ? 'border-top' : null;
            }
        },

        mounted() {
            if (!this.$overlay()) {
                console.error('ib-modal: Instantiate with $overlay');
            }
        },

        destroyed() {
            this.close();
        },

        methods: {
            /**
             * Display the dialog and block interaction outside of it.
             * @public
             */
            open() {
                if (!this.show) {
                    this.$refs.content && this.$refs.content.reset();
                    this._reset();
                    this.show = true;
                    this.$emit('open');
                }
            },

            /**
             * Close the dialog.
             * @public
             */
            close() {
                if (this.show) {
                    this.show = false;
                    this.$emit('close');
                    document.removeEventListener('focus', this.focus, true);
                    this._clear();
                    if (!--open) {
                        this._releasebody();
                    }
                }
            },

            /** @private */
            _reset() {
                if (!open++) {
                    this._capturebody();
                }
                if (this.$refs.mdef) {
                    this.$refs.mdef.scrollTop = 0;
                }
                document.addEventListener('focus', this.focus, true);
                this._clear();
                setTimeout(() => {
                    const content = this.$refs.content;
                    if (content) {
                        const focus = content.$el.querySelector('[autofocus]');
                        if (focus) {
                            focus.focus();
                        } else {
                            const focused = document.activeElement;
                            if (focused) {
                                focused.blur();
                            }
                        }
                    }
                }, 96);
            },

            /**
             * Clear selection range.
             * @private
             */
            _clear() {
                if (window.getSelection) {
                    window.getSelection().removeAllRanges();
                }
            },

            /**
             * Prevent body scrolling while modal is open.
             * @private
             */
            _capturebody() {
                const body = document.body;
                openx = window.pageXOffset;
                openy = window.pageYOffset;
                body.style.left = -openx + 'px';
                body.style.top = -openy + 'px';
                body.classList.add('_mopen');
            },

            /**
             * Allow body scrolling.
             * @private
             */
            _releasebody() {
                const body = document.body;
                body.style.left = '';
                body.style.top = '';
                body.classList.remove('_mopen');
                scrollTo(openx, openy);
            },

            /** @private */
            _mousedown(e) {
                if (e.currentTarget === e.target && !e.button) {
                    if (this.autoclose) {
                        this.close();
                    }
                }
            },

            /** @private */
            _touchstart(e) {
                if (e.currentTarget === e.target) {
                    if (this.autoclose) {
                        this.close();
                    }
                    e.preventDefault();
                }
            },

            /**
             * Called when the form is submitted and the action completes successfully.
             * @private
             */
            _success() {
                this.$emit('success');
                this.close();
            },

            /**
             * Called when the form is submitted but an exception occurs during the action.
             * @private
             */
            _error(error) {
                this.$emit('error', error);
            },

            /** Tab keyboard trap */

            keydown(e) {
                if (e.keyCode == 9) {
                    const els = $util.tabbableChildren(this.$el);
                    if (els.length) {
                        if (e.shiftKey) {
                            if (document.activeElement === els[0].el) {
                                e.preventDefault();
                                for (let i = els.length - 1; i >= 0; i--) {
                                    els[i].el.focus();
                                    if (document.activeElement === els[i].el) {
                                        break;
                                    }
                                }
                            }
                        } else {
                            if (document.activeElement === els[els.length - 1].el) {
                                e.preventDefault();
                                for (let i = 0; i < els.length; i++) {
                                    els[i].el.focus();
                                    if (document.activeElement === els[i].el) {
                                        break;
                                    }
                                }
                            }
                        }
                    }
                }
            },

            /** document.focus trap */

            focus(e) {
                if (this.$el === document.body.lastChild) {
                    if (!this.$el.contains(e.target)) {
                        const els = $util.tabbableChildren(this.$el);
                        for (let i = 0; i < els.length; i++) {
                            els[i].el.focus();
                            if (document.activeElement === els[i].el) {
                                break;
                            }
                        }
                    }
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    /** Body modifiers */
    body._mopen {
        position: fixed;
        min-width: 100%;
        min-height: 100%;
        -webkit-user-select: none;
           -moz-user-select: none;
            -ms-user-select: none;
                user-select: none;
    }

    ._modal {
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        z-index: @z-dialog;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        -webkit-user-select: text;
           -moz-user-select: text;
            -ms-user-select: text;
                user-select: text;

        /** Backdrop */
        &::before {
            content: '';
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background: @bg-backdrop;
            pointer-events: none;
        }
    }
    ._mdlg {
        display: flex;
        flex-direction: column;
        position: relative;
        width: @modal-width;
        max-width: 95%;
        max-height: 95%;
        overflow: hidden;
        .dlg-outer();
        & when not (@dialog-elevation = none) {
            .elevation(@dialog-elevation);
        }
        .bg {
            background: none;
        }
    }
    ._mdef {
        display: flex;
        flex: 1 1 auto;
        align-items: stretch;
        position: relative;
        overflow-y: auto;
        .dlg-main();
    }
    ._mbtn {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        flex: 0 0 auto;
        .dlg-footer();
    }

    /** Alert and Confirm */
    ._mdismiss {
        display: flex;
        justify-content: center;
        .dlg-dismiss();
    }

    /** Animation */
    @keyframes _tr-modal {
      from {
        pointer-events: none;
      } to {
        pointer-events: auto;
      }
    }
    ._tr-modal-enter-active {
      animation: _tr-modal .5s;
      .modal-animation();
    }
</style>
